import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router";

import { SubmitButton } from "@/components/Interface/Button/Button";
import { PasswordField } from "@/components/Interface/FormFields/PasswordField/PasswordField";
import { PasswordStrength } from "@/components/Interface/PasswordStrength/PasswordStrength";
import { BrandLoadingScreen } from "@/components/Layout/BrandLoadingScreen/BrandLoadingScreen";
import { LocalLoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { useAlert } from "@/hooks/useAlert";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { getGetCustomerQueryKey, useGetCustomer } from "@/services/api";
import { useChangePassword } from "@/services/auth";
import { AlertTypes } from "@/utils/atoms";
import { createInitialPasswordSetupErrorHandler } from "@/utils/initialPasswordSetupErrorHandler";
import {
  getPasswordRequirementTokens,
  getPasswordValidationError,
} from "@/utils/passwordValidation";

import { WelcomeCard } from "./WelcomeCard";

type InitialPasswordSetupForm = {
  confirmPassword: string;
  password: string;
};

export const InitialPasswordSetup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showAlert = useAlert();
  const { data: customer, isPending: isCustomerLoading } = useGetCustomer();
  const [showPasswordStrength, setShowPasswordStrength] = useState(false);

  const {
    handleSubmit,
    register,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<InitialPasswordSetupForm>({
    defaultValues: {
      confirmPassword: "",
      password: "",
    },
    mode: "onChange",
  });

  const passwordRequirements = useMemo(
    () => getPasswordRequirementTokens(t),
    [t],
  );

  const handleSetPasswordError = useMemo(
    () => createInitialPasswordSetupErrorHandler(setError, t, showAlert),
    [setError, showAlert, t],
  );

  const { mutate: setPassword, isPending: isSettingPassword } =
    useChangePassword({
      mutation: {
        onSuccess: () => {
          queryClient.setQueryData(
            getGetCustomerQueryKey(),
            (previousCustomer) =>
              previousCustomer
                ? {
                    ...previousCustomer,
                    initialPasswordChanged: true,
                  }
                : previousCustomer,
          );
          showAlert({
            type: AlertTypes.success,
            text: t(
              "user-account.personalDetails.loginData.passwordChangeSuccess",
            ),
          });
          navigate(portalRoutes.cockpit.base, { replace: true });
        },
        onError: handleSetPasswordError,
      },
    });

  const onSubmit = ({ password }: InitialPasswordSetupForm) => {
    setPassword({
      data: { newPassword: password },
    });
  };

  if (isCustomerLoading) {
    return <BrandLoadingScreen />;
  }

  if (customer?.initialPasswordChanged !== false) {
    return <Navigate to={portalRoutes.cockpit.base} replace />;
  }

  return (
    <>
      <div className="mt-4 flex justify-center px-4 pb-8">
        <WelcomeCard>
          <form
            className="flex w-full flex-col gap-6"
            onSubmit={handleSubmit(onSubmit)}
          >
            <h2 className="text-headline-md font-semibold leading-sm text-foreground-primary">
              {t("translation:newWelcome.initialPasswordSetup.title")}
            </h2>

            {isSettingPassword && <LocalLoadingSpinner />}

            <div className="flex flex-col gap-6">
              <div>
                <PasswordField<InitialPasswordSetupForm>
                  id="initial-password"
                  name="password"
                  label={t(
                    "translation:newWelcome.initialPasswordSetup.passwordLabel",
                  )}
                  autocomplete="new-password"
                  register={register}
                  errors={errors}
                  options={{
                    required: {
                      value: true,
                      message: t(
                        "translation:Common.generalForm.validation.passwordRequired",
                      ),
                    },
                    validate: (value: string) =>
                      getPasswordValidationError(value, t),
                  }}
                  onFocus={() => setShowPasswordStrength(true)}
                  onBlur={() => setShowPasswordStrength(false)}
                />
                {showPasswordStrength && (
                  <div className="mt-2">
                    <PasswordStrength
                      password={watch("password")}
                      validationTokens={passwordRequirements}
                      inverted={false}
                    />
                  </div>
                )}
              </div>

              <PasswordField<InitialPasswordSetupForm>
                id="confirm-initial-password"
                name="confirmPassword"
                label={t(
                  "translation:newWelcome.initialPasswordSetup.confirmPasswordLabel",
                )}
                autocomplete="new-password"
                register={register}
                errors={errors}
                options={{
                  required: {
                    value: true,
                    message: t(
                      "translation:newWelcome.registerForm.validation.confirmPasswordRequired",
                    ),
                  },
                  validate: (value: string) =>
                    value === watch("password") ||
                    t(
                      "translation:newWelcome.registerForm.validation.passwordNoMatch",
                    ),
                }}
                onFocus={() => setShowPasswordStrength(false)}
              />
            </div>

            {/* One inline indicator (above) covers the pending state - like
                PersonalDataOnboarding's sibling submit button, this one keeps
                its static label throughout and relies on `disabled` alone. */}
            <div className="flex justify-center">
              <SubmitButton
                className="accent capitalize"
                label={t("Common.label.continue")}
                disabled={isSettingPassword || isSubmitting}
                fullWidth={false}
              />
            </div>
          </form>
        </WelcomeCard>
      </div>
    </>
  );
};
